import { memo, useMemo, useState } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";

import Lock from "@material-ui/icons/Lock";
import { Input, Tooltip } from "antd";
import styled from "styled-components";

import ColorPicker from "components/base/ColorPicker";
import {
  CustomExpanderRoot,
  GroupedFeatureHeaderWrapper,
  HeaderContainer,
  ITEM_HEIGHT,
  StyledCheckbox,
  StyledGeomGroupTitle,
  TitleContainer
} from "components/project/shapefiles/components/shapefile-details/ShapefileGroupRowStyles";

import { EmptyStateMessage } from "./emptyStateMessage";

const SwatchWrapper = styled.span`
  position: relative;
  display: inline-flex;
`;

const LockOverlay = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: #ffffff;

  .MuiSvgIcon-root {
    font-size: 1.2rem;
  }
`;

export interface RigFracValueListProps {
  // Sorted attribute value domain.
  values: string[];
  // value → hex color (palette assignment with overrides applied) — identical
  // to the map coloring so swatches never disagree. Every domain value has an
  // entry because the map is built from the same domain.
  valueColors: Map<string, string>;
  isReadOnly: boolean;
  // Values that are unchecked (hidden on the map) for the active attribute.
  hiddenValues: Set<string>;
  // Values excluded by the layer-local filter (EVA-63) — shown unchecked, but
  // the checkbox stays toggleable (hidden state is independent of the filter).
  filteredOutValues?: Set<string>;
  // These values take their colour from the active well focus, so their own
  // colour cannot be edited.
  focusControlledValues?: Set<string>;
  onOverride: (value: string, color: string) => void;
  // Session-only visibility toggle; checked = shown, unchecked = hidden.
  onToggleValue: (value: string, checked: boolean) => void;
  // Display text for a value; the value itself still keys every callback.
  formatLabel?: (value: string) => string;
}

// The row's data payload matches the list props (with `values` narrowed to the
// filtered subset), so there's no second shape to keep in sync.
type RowData = RigFracValueListProps;

// Defined at module scope and memoized so untouched rows skip re-rendering when
// the parent updates (with the parent's stable handlers, only rows whose value
// actually changed re-render). Renders the shapefile group row's own components
// (CustomExpanderRoot/HeaderContainer/GroupedFeatureHeaderWrapper/TitleContainer/
// StyledCheckbox/StyledGeomGroupTitle) so the two panels' rows are pixel-identical
// by construction, with the chevron/expander column simply omitted.
const Row = memo(function Row({
  index,
  style,
  data
}: {
  index: number;
  style: React.CSSProperties;
  data: RowData;
}) {
  const {
    values,
    valueColors,
    isReadOnly,
    hiddenValues,
    filteredOutValues,
    focusControlledValues,
    onOverride,
    onToggleValue,
    formatLabel
  } = data;
  const value = values[index];
  const label = formatLabel?.(value) ?? value;
  // Every domain value is present in valueColors (built from the same domain).
  const color = valueColors.get(value) as string;
  const isFilteredOut = !!filteredOutValues?.has(value);
  const isFocusControlled = !!focusControlledValues?.has(value);
  // The checkbox is bound to the hidden set ALONE so it round-trips. A value the
  // layer filter excludes renders unchecked + disabled (the filter is the
  // authority; the checkbox can't represent two independent states), with the
  // row dimmed and a tooltip.
  const isChecked = !isFilteredOut && !hiddenValues.has(value);

  return (
    <CustomExpanderRoot style={isFilteredOut ? { ...style, opacity: 0.5 } : style}>
      <HeaderContainer>
        <GroupedFeatureHeaderWrapper>
          <Tooltip title={isFilteredOut ? "Excluded by the layer filter" : undefined}>
            <span>
              <StyledCheckbox
                checked={isChecked}
                disabled={isFilteredOut}
                onChange={(e) => onToggleValue(value, e.target.checked)}
                data-testid="value-checkbox"
              />
            </span>
          </Tooltip>
          <Tooltip
            title={
              isFocusControlled
                ? "Colour follows the Focus bin for this value. Change it in the Focus panel."
                : undefined
            }>
            <SwatchWrapper>
              <ColorPicker
                isReadOnly={isReadOnly || isFocusControlled}
                hasOutline={false}
                includeOpacity={false}
                values={{
                  color,
                  strokeColor: color,
                  thickness: 2,
                  opacity: 100
                }}
                onChange={(_prop, newColor) => onOverride(value, newColor as string)}
              />
              {isFocusControlled && (
                <LockOverlay>
                  <Lock
                    role="img"
                    aria-label="Colour follows the Focus bin for this value. Change it in the Focus panel."
                  />
                </LockOverlay>
              )}
            </SwatchWrapper>
          </Tooltip>
        </GroupedFeatureHeaderWrapper>
        <TitleContainer>
          <GroupedFeatureHeaderWrapper>
            <StyledGeomGroupTitle title={label}>{label}</StyledGeomGroupTitle>
          </GroupedFeatureHeaderWrapper>
        </TitleContainer>
      </HeaderContainer>
    </CustomExpanderRoot>
  );
});

export function RigFracValueList({
  values,
  valueColors,
  isReadOnly,
  hiddenValues,
  filteredOutValues,
  focusControlledValues,
  onOverride,
  onToggleValue,
  formatLabel
}: RigFracValueListProps): JSX.Element {
  const [searchText, setSearchText] = useState("");

  const filteredValues = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) {
      return values;
    }
    return values.filter((value) =>
      (formatLabel?.(value) ?? value).toLowerCase().includes(term)
    );
  }, [values, searchText, formatLabel]);

  // Stable object so react-window doesn't hand every row a new reference each
  // render, which (with the memoized Row) is what lets untouched rows skip.
  const itemData: RowData = useMemo(
    () => ({
      values: filteredValues,
      valueColors,
      isReadOnly,
      hiddenValues,
      filteredOutValues,
      focusControlledValues,
      onOverride,
      onToggleValue,
      formatLabel
    }),
    [
      filteredValues,
      valueColors,
      isReadOnly,
      hiddenValues,
      filteredOutValues,
      focusControlledValues,
      onOverride,
      onToggleValue,
      formatLabel
    ]
  );

  return (
    <ListContainer>
      <SearchContainer>
        <Input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search"
          allowClear
          data-testid="rig-frac-value-search"
        />
      </SearchContainer>
      <ListBody>
        {filteredValues.length === 0 ? (
          <EmptyStateMessage>No values match your search.</EmptyStateMessage>
        ) : (
          <AutoSizer>
            {({ height, width }) => (
              <FixedSizeList
                height={height}
                width={width}
                itemCount={filteredValues.length}
                itemSize={ITEM_HEIGHT}
                itemData={itemData}>
                {Row}
              </FixedSizeList>
            )}
          </AutoSizer>
        )}
      </ListBody>
    </ListContainer>
  );
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;

const SearchContainer = styled.div`
  padding: 10px 14px 0px 14px;

  .ant-input-affix-wrapper {
    width: 100%;
  }
`;

// Matches the shapefile list's FeaturesContainer inset (10px 14px) so the two
// lists sit the same distance from the panel edges.
const ListBody = styled.div`
  flex: 1;
  min-height: 0;
  padding: 10px 14px;
`;
